/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  ManualPlacementEntry,
  QuestionOptionPayload,
  QuestionOptionsContextValue,
  QuestionOptionsStateRecord,
  QuestionType,
  OptionsKey,
  DifficultyLevel,
} from '@/types';
import { useAuth } from '@/hooks/useAuth';
import { authService } from '@/services/auth';
import {
  createManualPlacementKey,
  QUESTION_OPTIONS_STORAGE_VERSION,
} from '@/types';
import {
  clearQuestionOptionsState,
  loadQuestionOptionsState,
  saveQuestionOptionsState,
  readQuestionOptionsDraft,
  writeQuestionOptionsDraft,
} from '@/services/questionOptionsService';
import { debugEvent } from '@/lib/debugApi';
import { updateCachedQuestionOptionsSeed } from '@/components/providers/editorSeedCache';

interface QuestionOptionsProviderProps {
  children: React.ReactNode;
  // When provided (from editor-init), skip the initial server fetch
  initialState?: QuestionOptionsStateRecord;
}

export const QuestionOptionsContext = createContext<
  QuestionOptionsContextValue | undefined
>(undefined);

const createDefaultState = (): QuestionOptionsStateRecord => ({
  options: {},
  manualPlacements: {},
  updatedAt: new Date().toISOString(),
  version: QUESTION_OPTIONS_STORAGE_VERSION,
});

export function QuestionOptionsProvider({
  children,
  initialState,
}: QuestionOptionsProviderProps) {
  const { user, isImpersonating } = useAuth();
  const userId = user?.id;
  const [state, setState] = useState<QuestionOptionsStateRecord>(() =>
    initialState ?? createDefaultState()
  );
  const [isLoading, setIsLoading] = useState<boolean>(!initialState);
  const [error, setError] = useState<Error | null>(null);
  const stateRef = useRef(state);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveAttempt, setSaveAttempt] = useState(0);
  const ownerRef = useRef(user?.id);
  ownerRef.current = user?.id;
  const skipNextPersistRef = useRef<boolean>(true);

  useEffect(() => {
    // Skip hydration if initial state was provided from editor-init
    if (initialState) {
      const draft = !isImpersonating ? readQuestionOptionsDraft(user?.id) : undefined;
      skipNextPersistRef.current = !draft;
      stateRef.current = draft ?? initialState;
      setState(stateRef.current);
      setLoadFailed(false);
      setError(null);
      setIsLoading(false);
      debugEvent('QuestionOptions.hydrate:skip-seeded');
      return;
    }

    let cancelled = false;

    const hydrate = async () => {
      // Skip loading if no token (user not authenticated)
      const token = authService.getToken();
      debugEvent('QuestionOptions.hydrate:start', { hasToken: Boolean(token) });
      if (!token) {
        skipNextPersistRef.current = true;
        setError(null);
        setLoadFailed(false);
        setIsLoading(false);
        stateRef.current = createDefaultState();
        setState(stateRef.current);
        debugEvent('QuestionOptions.hydrate:skip');
        return;
      }

      setIsLoading(true);
      try {
        const loaded = await loadQuestionOptionsState();
        if (!cancelled) {
          const draft = !isImpersonating ? readQuestionOptionsDraft(user?.id) : undefined;
          skipNextPersistRef.current = !draft;
          stateRef.current = draft ?? loaded;
          setState(stateRef.current);
          setLoadFailed(false);
          setError(null);
          debugEvent('QuestionOptions.hydrate:success', {
            updatedAt: loaded.updatedAt,
            version: loaded.version,
          });
        }
      } catch (err) {
        console.error('[QuestionOptionsProvider] Failed to load state:', err);
        if (!cancelled) {
          setError(err as Error);
          setLoadFailed(true);
          debugEvent('QuestionOptions.hydrate:error', {
            error: err instanceof Error ? err.message : String(err),
          });
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          debugEvent('QuestionOptions.hydrate:done');
        }
      }
    };

    void hydrate();

    return () => {
      cancelled = true;
    };
  }, [initialState, user?.id, isImpersonating]);

  useEffect(() => {
    if (isLoading || loadFailed || userId === undefined || isImpersonating) return;
    if (!authService.getToken()) return;
    if (skipNextPersistRef.current || stateRef.current !== state) return;

    const owner = userId;
    setIsSaving(true);
    setError(null);
    // Start immediately; route unmounts must not cancel a scheduled save.
    void saveQuestionOptionsState(state, owner).then(() => {
      if (ownerRef.current === owner && stateRef.current === state) {
        skipNextPersistRef.current = true;
        setError(null);
        setIsSaving(false);
      }
    }).catch((err: Error) => {
      if (ownerRef.current === owner && stateRef.current === state) {
        setError(err);
        setIsSaving(false);
      }
    });
  }, [state, isLoading, loadFailed, userId, isImpersonating, saveAttempt]);

  useEffect(() => {
    if (!isSaving && !error) return;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      if (skipNextPersistRef.current) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [isSaving, error]);

  // Keep the EditorProviders module-level seed cache in sync with the
  // current in-memory state. Without this, navigating away from a route
  // that mounts EditorProviders and back again rehydrates child providers
  // from a stale editor-init snapshot, visually "losing" the user's edits
  // until the next full page refresh re-fetches editor-init.
  useEffect(() => {
    if (isLoading || loadFailed || isImpersonating) return;
    if (!user) return;
    updateCachedQuestionOptionsSeed(user.id, state);
  }, [state, isLoading, loadFailed, isImpersonating, user]);

  const updateState = useCallback(
    (
      reducer: (
        prev: QuestionOptionsStateRecord
      ) => QuestionOptionsStateRecord
    ) => {
      if (loadFailed || isLoading) return stateRef.current;
      const next = {
        ...reducer(stateRef.current),
        version: QUESTION_OPTIONS_STORAGE_VERSION,
        updatedAt: new Date().toISOString(),
      };
      skipNextPersistRef.current = false;
      stateRef.current = next;
      if (!isImpersonating) writeQuestionOptionsDraft(user?.id, next);
      setState(next);
      return next;
    },
    [user?.id, isImpersonating, loadFailed, isLoading]
  );

  const getOption = useCallback(
    (type: OptionsKey) => state.options[type],
    [state.options]
  );

  const setOption = useCallback(
    (type: OptionsKey, payload: QuestionOptionPayload) => {
      updateState((prev) => ({
        ...prev,
        options: { ...prev.options, [type]: payload },
      }));
    },
    [updateState]
  );

  const mergeOption = useCallback(
    (type: OptionsKey, patch: QuestionOptionPayload) => {
      let merged: QuestionOptionPayload = patch;
      updateState((prev) => {
        const existing = prev.options[type] ?? {};
        merged = { ...existing, ...patch };
        return {
          ...prev,
          options: { ...prev.options, [type]: merged },
        };
      });
      return merged;
    },
    [updateState]
  );

  const clearOption = useCallback(
    (type: OptionsKey) => {
      updateState((prev) => {
        if (!prev.options[type] && type !== '동형문제') return prev;
        const nextOptions = { ...prev.options };
        if (type === '동형문제') nextOptions[type] = {};
        else delete nextOptions[type];
        return {
          ...prev,
          options: nextOptions,
        };
      });
    },
    [updateState]
  );

  const setManualPlacement = useCallback(
    (entry: ManualPlacementEntry) => {
      const key =
        entry.key ||
        createManualPlacementKey(
          entry.passageId,
          entry.questionType,
          entry.difficulty
        );

      updateState((prev) => ({
        ...prev,
        manualPlacements: {
          ...prev.manualPlacements,
          [key]: {
            ...entry,
            key,
            updatedAt: new Date().toISOString(),
          },
        },
      }));
    },
    [updateState]
  );

  const removeManualPlacement = useCallback(
    (key: string) => {
      updateState((prev) => {
        if (!prev.manualPlacements[key]) return prev;
        const nextManualPlacements = { ...prev.manualPlacements };
        delete nextManualPlacements[key];
        return {
          ...prev,
          manualPlacements: nextManualPlacements,
        };
      });
    },
    [updateState]
  );

  const getManualPlacementKey = useCallback(
    (
      passageId: string,
      questionType: QuestionType,
      difficulty: DifficultyLevel
    ) => createManualPlacementKey(passageId, questionType, difficulty),
    []
  );

  const resetAllOptions = useCallback(
    (type?: OptionsKey) => {
      if (!type) {
        updateState(() => ({ ...createDefaultState(), options: { 동형문제: {} } }));
        return;
      }

      updateState((prev) => {
        if (!prev.options[type] && type !== '동형문제') {
          return prev;
        }
        const nextOptions = { ...prev.options };
        if (type === '동형문제') nextOptions[type] = {};
        else delete nextOptions[type];
        return {
          ...prev,
          options: nextOptions,
        };
      });
    },
    [updateState]
  );

  const refresh = useCallback(async () => {
    const owner = user?.id;
    try {
      setIsLoading(true);
      const loaded = await loadQuestionOptionsState();
      if (ownerRef.current !== owner) return;
      const draft = !isImpersonating ? readQuestionOptionsDraft(owner) : undefined;
      skipNextPersistRef.current = !draft;
      stateRef.current = draft ?? loaded;
      setState(stateRef.current);
      setLoadFailed(false);
      setError(null);
    } catch (err) {
      if (ownerRef.current === owner) setError(err as Error);
    } finally {
      if (ownerRef.current === owner) setIsLoading(false);
    }
  }, [user?.id, isImpersonating]);

  const contextValue = useMemo<QuestionOptionsContextValue>(
    () => ({
      isLoading,
      error,
      options: state.options,
      manualPlacements: state.manualPlacements,
      getOption,
      setOption,
      mergeOption,
      clearOption,
      setManualPlacement,
      removeManualPlacement,
      getManualPlacementKey,
      resetAllOptions,
      refresh,
    }),
    [
      isLoading,
      error,
      state.options,
      state.manualPlacements,
      getOption,
      setOption,
      mergeOption,
      clearOption,
      setManualPlacement,
      removeManualPlacement,
      getManualPlacementKey,
      resetAllOptions,
      refresh,
    ]
  );

  return (
    <QuestionOptionsContext.Provider value={contextValue}>
      {error && (
        <div role="alert" className="m-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {loadFailed ? '출제 옵션을 불러오지 못했습니다. 다시 불러온 뒤 계속해주세요.' : '출제 옵션을 서버에 저장하지 못했습니다. 변경 내용을 유지한 채 다시 저장해주세요.'}
          <button type="button" className="ml-3 underline" onClick={() => loadFailed ? void refresh() : setSaveAttempt(value => value + 1)}>
            {loadFailed ? '다시 불러오기' : '다시 저장'}
          </button>
        </div>
      )}
      {isLoading ? <div role="status" className="p-4 text-sm">출제 옵션 불러오는 중…</div> : !loadFailed && children}
    </QuestionOptionsContext.Provider>
  );
}

export function useQuestionOptions(): QuestionOptionsContextValue {
  const ctx = useContext(QuestionOptionsContext);
  if (!ctx) {
    throw new Error(
      'useQuestionOptions must be used within a QuestionOptionsProvider'
    );
  }
  return ctx;
}

export async function resetQuestionOptionsState() {
  await clearQuestionOptionsState();
}
